<template>
  <div class="ai-settings-page">
    <v-container fluid>
      <!-- Header -->
      <div class="d-flex align-center justify-space-between mb-6">
        <div>
          <h1 class="text-h4 text-gradient mb-2">
            <v-icon class="me-2" size="36">mdi-robot</v-icon>
            {{ $t('aiSettings.title') }}
          </h1>
          <p class="text-subtitle-1 text-medium-emphasis">
            {{ $t('aiSettings.subtitle') }}
          </p>
        </div>
      </div>

      <v-row>
        <!-- API Configuration Card -->
        <v-col cols="12" md="6">
          <v-card elevation="2" class="mb-4">
            <v-card-title class="d-flex align-center">
              <v-icon class="me-2" color="primary">mdi-key-variant</v-icon>
              {{ $t('aiSettings.apiConfig') }}
            </v-card-title>
            <v-divider />
            <v-card-text class="pa-6">
              <!-- Provider -->
              <v-select
                v-model="settings.provider"
                :items="providers"
                item-title="label"
                item-value="value"
                :label="$t('aiSettings.provider')"
                variant="outlined"
                prepend-inner-icon="mdi-cloud"
                class="mb-4"
                disabled
              />

              <!-- API Key -->
              <v-text-field
                v-model="settings.apiKey"
                :label="$t('aiSettings.apiKey')"
                variant="outlined"
                prepend-inner-icon="mdi-key"
                :append-inner-icon="showApiKey ? 'mdi-eye-off' : 'mdi-eye'"
                :type="showApiKey ? 'text' : 'password'"
                @click:append-inner="showApiKey = !showApiKey"
                :placeholder="settings.hasApiKey ? '****' + $t('aiSettings.apiKeySet') : $t('aiSettings.apiKeyPlaceholder')"
                class="mb-4"
                :hint="settings.hasApiKey ? $t('aiSettings.apiKeyExists') : ''"
                persistent-hint
              />

              <!-- Model Selection -->
              <v-select
                v-model="settings.model"
                :items="models"
                item-title="label"
                item-value="value"
                :label="$t('aiSettings.model')"
                variant="outlined"
                prepend-inner-icon="mdi-brain"
                class="mb-4"
              />

              <!-- Max Duration -->
              <v-slider
                v-model="settings.maxDuration"
                :min="5"
                :max="30"
                :step="5"
                :label="$t('aiSettings.maxDuration')"
                thumb-label="always"
                class="mb-4"
              >
                <template #append>
                  <span class="text-body-2">{{ settings.maxDuration }}с</span>
                </template>
              </v-slider>

              <!-- Enable/Disable -->
              <v-switch
                v-model="settings.isEnabled"
                :label="settings.isEnabled ? $t('aiSettings.enabled') : $t('aiSettings.disabled')"
                color="success"
                inset
                class="mb-2"
              />

              <div class="d-flex gap-2">
                <v-btn
                  color="primary"
                  :loading="saving"
                  @click="saveSettings"
                >
                  <v-icon start>mdi-content-save</v-icon>
                  {{ $t('aiSettings.save') }}
                </v-btn>

                <v-btn
                  color="info"
                  variant="outlined"
                  :loading="testing"
                  @click="testConnection"
                >
                  <v-icon start>mdi-connection</v-icon>
                  {{ $t('aiSettings.testConnection') }}
                </v-btn>
              </div>

              <!-- Connection test result -->
              <v-alert
                v-if="testResult"
                :type="testResult.success ? 'success' : 'error'"
                class="mt-4"
                density="compact"
                closable
                @click:close="testResult = null"
              >
                {{ testResult.message }}
              </v-alert>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Generation Jobs Card -->
        <v-col cols="12" md="6">
          <v-card elevation="2" class="mb-4">
            <v-card-title class="d-flex align-center justify-space-between">
              <div class="d-flex align-center">
                <v-icon class="me-2" color="orange">mdi-video-plus</v-icon>
                {{ $t('aiSettings.recentJobs') }}
              </div>
              <v-btn size="small" variant="text" @click="loadJobs" :loading="loadingJobs">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-card-title>
            <v-divider />
            <v-card-text class="pa-0">
              <v-list v-if="jobs.length" density="compact">
                <v-list-item
                  v-for="job in jobs"
                  :key="job.id"
                  class="py-2"
                >
                  <template #prepend>
                    <v-icon
                      :color="getStatusColor(job.status)"
                      size="small"
                    >
                      {{ getStatusIcon(job.status) }}
                    </v-icon>
                  </template>

                  <v-list-item-title class="text-body-2">
                    <v-chip
                      :color="job.type === 'generate' ? 'primary' : 'orange'"
                      size="x-small"
                      class="me-1"
                    >
                      {{ job.type === 'generate' ? 'Жаңа' : 'Өзгерту' }}
                    </v-chip>
                    {{ truncatePrompt(job.prompt) }}
                  </v-list-item-title>

                  <v-list-item-subtitle class="text-caption">
                    {{ formatDate(job.createdAt) }}
                    <v-chip
                      :color="getStatusColor(job.status)"
                      size="x-small"
                      class="ms-1"
                    >
                      {{ getStatusLabel(job.status) }}
                    </v-chip>
                  </v-list-item-subtitle>

                  <template #append>
                    <div class="d-flex gap-1">
                      <!-- Check status for running jobs -->
                      <v-btn
                        v-if="job.status === 'running'"
                        icon="mdi-refresh"
                        size="x-small"
                        variant="text"
                        @click="checkJobStatus(job.id)"
                        :loading="checkingJob === job.id"
                      />

                      <!-- Download completed -->
                      <v-btn
                        v-if="job.status === 'completed' && job.resultUrl && !job.resultPath"
                        icon="mdi-download"
                        size="x-small"
                        variant="text"
                        color="success"
                        @click="downloadJob(job.id)"
                        :loading="downloadingJob === job.id"
                      />

                      <!-- Cancel/Delete -->
                      <v-btn
                        icon="mdi-close"
                        size="x-small"
                        variant="text"
                        color="error"
                        @click="deleteJob(job.id)"
                      />
                    </div>
                  </template>
                </v-list-item>
              </v-list>

              <div v-else class="pa-6 text-center text-medium-emphasis">
                <v-icon size="48" class="mb-2">mdi-video-off</v-icon>
                <div>{{ $t('aiSettings.noJobs') }}</div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Quick Generate Card -->
          <v-card elevation="2">
            <v-card-title class="d-flex align-center">
              <v-icon class="me-2" color="success">mdi-creation</v-icon>
              {{ $t('aiSettings.quickGenerate') }}
            </v-card-title>
            <v-divider />
            <v-card-text class="pa-6">
              <v-textarea
                v-model="generatePrompt"
                :label="$t('aiSettings.prompt')"
                variant="outlined"
                rows="3"
                auto-grow
                :placeholder="$t('aiSettings.promptPlaceholder')"
                class="mb-3"
              />

              <v-slider
                v-model="generateDuration"
                :min="5"
                :max="settings.maxDuration || 10"
                :step="5"
                :label="$t('aiSettings.duration')"
                thumb-label
                class="mb-3"
              />

              <v-btn
                color="success"
                block
                :loading="generating"
                :disabled="!generatePrompt || !settings.isEnabled"
                @click="quickGenerate"
              >
                <v-icon start>mdi-video-plus</v-icon>
                {{ $t('aiSettings.generateVideo') }}
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '@/utils/api'

const { t } = useI18n()

const settings = ref({
  provider: 'runway',
  apiKey: '',
  isEnabled: false,
  model: 'gen3a_turbo',
  maxDuration: 10,
  hasApiKey: false
})

const showApiKey = ref(false)
const saving = ref(false)
const testing = ref(false)
const testResult = ref(null)

const jobs = ref([])
const loadingJobs = ref(false)
const checkingJob = ref(null)
const downloadingJob = ref(null)

const generatePrompt = ref('')
const generateDuration = ref(10)
const generating = ref(false)

const snackbar = ref({ show: false, text: '', color: 'success' })

const providers = [
  { label: 'Runway ML', value: 'runway' }
]

const models = [
  { label: 'Gen-3 Alpha Turbo', value: 'gen3a_turbo' },
  { label: 'Gen-4 Turbo', value: 'gen4_turbo' }
]

function showSnack(text, color = 'success') {
  snackbar.value = { show: true, text, color }
}

function truncatePrompt(text) {
  if (!text) return '—'
  return text.length > 60 ? text.slice(0, 60) + '...' : text
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return d.toLocaleDateString('kk-KZ') + ' ' + d.toLocaleTimeString('kk-KZ', { hour: '2-digit', minute: '2-digit' })
}

function getStatusColor(status) {
  const map = { pending: 'grey', running: 'blue', completed: 'success', failed: 'error' }
  return map[status] || 'grey'
}

function getStatusIcon(status) {
  const map = { pending: 'mdi-clock-outline', running: 'mdi-loading mdi-spin', completed: 'mdi-check-circle', failed: 'mdi-alert-circle' }
  return map[status] || 'mdi-help-circle'
}

function getStatusLabel(status) {
  const map = { pending: 'Күтуде', running: 'Жүруде', completed: 'Дайын', failed: 'Қате' }
  return map[status] || status
}

// Load settings from server
async function loadSettings() {
  try {
    const res = await api.get('/admin/ai/settings')
    const data = res.data
    settings.value = {
      provider: data.provider || 'runway',
      apiKey: '',
      isEnabled: data.isEnabled || false,
      model: data.model || 'gen3a_turbo',
      maxDuration: data.maxDuration || 10,
      hasApiKey: data.hasApiKey || false
    }
  } catch (e) {
    console.error('Failed to load AI settings:', e)
  }
}

async function saveSettings() {
  saving.value = true
  try {
    const payload = {
      isEnabled: settings.value.isEnabled,
      model: settings.value.model,
      maxDuration: settings.value.maxDuration
    }
    // Only send API key if user typed a new one
    if (settings.value.apiKey && settings.value.apiKey.length > 6) {
      payload.apiKey = settings.value.apiKey
    }

    const res = await api.put('/admin/ai/settings', payload)
    settings.value.hasApiKey = res.data.settings?.hasApiKey || false
    settings.value.apiKey = '' // Clear input after save
    showSnack(t('aiSettings.saved'))
  } catch (e) {
    showSnack(e.response?.data?.error || 'Қате', 'error')
  } finally {
    saving.value = false
  }
}

async function testConnection() {
  testing.value = true
  testResult.value = null
  try {
    const res = await api.post('/admin/ai/test-connection')
    testResult.value = res.data
  } catch (e) {
    testResult.value = { success: false, message: e.response?.data?.error || 'Қосылу қатесі' }
  } finally {
    testing.value = false
  }
}

async function loadJobs() {
  loadingJobs.value = true
  try {
    const res = await api.get('/admin/ai/jobs', { params: { limit: 15 } })
    jobs.value = res.data.jobs || []
  } catch (e) {
    console.error('Failed to load jobs:', e)
  } finally {
    loadingJobs.value = false
  }
}

async function checkJobStatus(id) {
  checkingJob.value = id
  try {
    const res = await api.get(`/admin/ai/jobs/${id}/status`)
    const idx = jobs.value.findIndex(j => j.id === id)
    if (idx >= 0) {
      jobs.value[idx] = { ...jobs.value[idx], ...res.data }
    }
  } catch (e) {
    showSnack('Статус тексеру қатесі', 'error')
  } finally {
    checkingJob.value = null
  }
}

async function downloadJob(id) {
  downloadingJob.value = id
  try {
    const res = await api.post(`/admin/ai/jobs/${id}/download`)
    const idx = jobs.value.findIndex(j => j.id === id)
    if (idx >= 0) {
      jobs.value[idx].resultPath = res.data.filepath
    }
    showSnack('Видео жүктелді!')
  } catch (e) {
    showSnack('Жүктеу қатесі', 'error')
  } finally {
    downloadingJob.value = null
  }
}

async function deleteJob(id) {
  try {
    await api.delete(`/admin/ai/jobs/${id}`)
    jobs.value = jobs.value.filter(j => j.id !== id)
    showSnack('Жойылды')
  } catch (e) {
    showSnack('Жою қатесі', 'error')
  }
}

async function quickGenerate() {
  generating.value = true
  try {
    const res = await api.post('/admin/ai/generate', {
      prompt: generatePrompt.value,
      duration: generateDuration.value,
      model: settings.value.model
    })
    showSnack(`Генерация басталды! Job #${res.data.jobId}`)
    generatePrompt.value = ''
    loadJobs()
  } catch (e) {
    showSnack(e.response?.data?.error || 'Генерация қатесі', 'error')
  } finally {
    generating.value = false
  }
}

onMounted(() => {
  loadSettings()
  loadJobs()
})
</script>

<style scoped>
.ai-settings-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  padding: 20px;
}

.text-gradient {
  background: linear-gradient(135deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.gap-2 { gap: 8px; }
.gap-1 { gap: 4px; }
</style>
